import { LiveData, Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import { PublicUserService } from '../../cloud/services/public-user';
import type { WorkspacePresenceStore } from '../../organize';
import type { WorkspaceService } from '../../workspace';

export interface SearchPerson {
  id: string;
  name: string;
  docIds: readonly string[];
}

/** Identity discovery uses accessible workspace presence, never cursor data. */
export class PeopleSearchService extends Service {
  constructor(
    private readonly presence: WorkspacePresenceStore,
    private readonly server: WorkspaceServerService,
    workspace: WorkspaceService
  ) {
    super();
    if (workspace.workspace.flavour !== 'local')
      presence.startListening(workspace.workspace.id);
    const requested = new Set<string>();
    const resolveNames = () => {
      const current = server.server;
      if (!current) return;
      const users = current.scope.get(PublicUserService);
      for (const ids of presence.docPresence$.value.values())
        for (const id of ids) {
          if (id.startsWith('external-doc-access:') || requested.has(id))
            continue;
          requested.add(id);
          if (!users.publicUser$(id).value) users.revalidate(id);
        }
    };
    const subscription = presence.docPresence$.subscribe(resolveNames);
    const serverSubscription = server.server$.subscribe(() => {
      requested.clear();
      resolveNames();
    });
    this.disposables.push(() => {
      subscription.unsubscribe();
      serverSubscription.unsubscribe();
    });
  }

  readonly people$ = LiveData.computed(get => {
    const docs = get(this.presence.docPresence$);
    const names = get(this.presence.docPresenceNames$);
    const users = get(this.server.server$)?.scope.get(PublicUserService);
    const people = new Map<string, SearchPerson>();
    for (const [docId, ids] of docs)
      for (const id of ids) {
        const user = id.startsWith('external-doc-access:')
          ? null
          : users
            ? get(users.publicUser$(id))
            : null;
        if (user?.removed) continue;
        const name =
          names.get(docId)?.get(id) ||
          (user && !user.removed ? user.name : null);
        if (!name?.trim()) continue;
        const previous = people.get(id);
        people.set(id, {
          id,
          name,
          docIds: [...(previous?.docIds ?? []), docId],
        });
      }
    return [...people.values()].sort((a, b) => a.name.localeCompare(b.name));
  });
}
